import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRegistry, ServiceDog } from '../context/RegistryContext';
import { Save, ArrowLeft, AlertCircle } from 'lucide-react';

type FormErrors = {
  [key: string]: string;
};

const AdminEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getServiceDogById, updateServiceDog } = useRegistry();

  const [formData, setFormData] = useState<ServiceDog | null>(null);
  const [originalData, setOriginalData] = useState<ServiceDog | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch service dog data
  useEffect(() => {
    const fetchDog = async () => {
      if (!id) {
        setErrors({ form: 'Invalid service dog ID' });
        setLoading(false);
        return;
      }

      try {
        const dog = await getServiceDogById(id);

        if (!dog) {
          setErrors({ form: 'Service dog not found' });
        } else {
          setFormData(dog);
          setOriginalData(dog);
          setPhotoPreview(dog.dogPhoto);
        }
      } catch (error) {
        console.error('Error fetching service dog:', error);
        setErrors({ form: 'An error occurred while retrieving the service dog information' });
      } finally {
        setLoading(false);
      }
    };

    fetchDog();
  }, [id, getServiceDogById]);

  // Track form changes
  useEffect(() => {
    if (formData && originalData) {
      // Compare current form data with original data to detect changes
      const changed = Object.keys(formData).some(key => {
        // Skip certain fields from comparison
        if (['id', 'registryNumber'].includes(key)) return false;

        // @ts-ignore
        return formData[key] !== originalData[key];
      });

      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Handle form field changes
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (!formData) return;

    setFormData(prev => {
      if (!prev) return null;
      return { ...prev, [name]: value };
    });

    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle photo change
  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !formData) return;

    // Validate file size and type
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setErrors(prev => ({ ...prev, dogPhoto: 'Photo must be less than 5MB' }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, dogPhoto: 'File must be an image' }));
      return;
    }

    // Create file preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setPhotoPreview(event.target.result as string);
        setFormData(prev => {
          if (!prev) return null;
          return { ...prev, dogPhoto: event.target?.result as string };
        });

        // Clear error
        if (errors.dogPhoto) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.dogPhoto;
            return newErrors;
          });
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Validate form
  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData) return false;

    if (!formData.dogName.trim()) newErrors.dogName = 'Dog name is required';
    if (!formData.dogBreed.trim()) newErrors.dogBreed = 'Dog breed is required';
    if (!formData.dogAge.trim()) newErrors.dogAge = 'Dog age is required';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (!formData.ownerName.trim()) newErrors.ownerName = 'Owner name is required';
    if (!formData.ownerAddress.trim()) newErrors.ownerAddress = 'Address is required';
    if (!formData.ownerPhone.trim()) newErrors.ownerPhone = 'Phone number is required';

    if (!formData.ownerEmail.trim()) {
      newErrors.ownerEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData) return;

    if (!validateForm()) return;

    setSaving(true);

    try {
      console.log('Updating dog with ID:', formData.id, 'Status:', formData.status);

      // Check if status has changed
      const statusChanged = originalData && originalData.status !== formData.status;
      if (statusChanged) {
        console.log(`Status changed from ${originalData?.status} to ${formData.status}`);
      }

      // Update the service dog in the registry
      const updatedDog = await updateServiceDog(formData);
      console.log('Dog updated successfully:', updatedDog);

      // Redirect to admin dashboard
      navigate('/admin/dashboard');
    } catch (error: any) {
      console.error('Error updating service dog:', error);

      // Provide more specific error messages
      if (error.message && error.message.includes('not found')) {
        setErrors(prev => ({
          ...prev,
          form: `Dog record not found. This may happen if the dog was deleted or if there are permission issues.`
        }));
      } else if (error.code === 'permission-denied') {
        setErrors(prev => ({
          ...prev,
          form: 'Permission denied. Make sure you are logged in as an admin user.'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          form: `An error occurred while saving: ${error.message || 'Unknown error'}. Please try again.`
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-primary-200 h-12 w-12 mb-4"></div>
          <div className="h-4 bg-primary-200 rounded w-32 mb-3"></div>
          <div className="h-3 bg-primary-100 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8 animate-fade-in">
        <div className="flex items-start text-accent-600 mb-6">
          <AlertCircle size={28} className="mr-3 mt-0.5" />
          <div>
            <h1 className="text-xl font-bold">Error</h1>
            <p className="text-neutral-600 mt-1">{errors.form || 'Unable to load service dog information'}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg shadow transition-colors"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center text-primary-600 hover:text-primary-800 mr-4"
        >
          <ArrowLeft size={20} className="mr-1" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-2xl font-bold text-primary-800">
          Edit Service Dog Record
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-primary-600 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">
              {formData.dogName} ({formData.registryNumber})
            </h2>
            <p className="text-primary-100 mt-1">
              Edit registry information
            </p>
          </div>

          <div>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={`px-3 py-1 rounded font-medium text-sm
                ${formData.status === 'active' ? 'bg-success-600 hover:bg-success-700' : ''}
                ${formData.status === 'pending' ? 'bg-warning-600 hover:bg-warning-700' : ''}
                ${formData.status === 'revoked' ? 'bg-error-600 hover:bg-error-700' : ''}
                text-white border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50
              `}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Global error */}
          {errors.form && (
            <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{errors.form}</span>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:space-x-6">
            {/* Left column - Dog Info */}
            <div className="flex-1 mb-6 md:mb-0">
              <h3 className="text-lg font-semibold text-primary-700 mb-4 border-b border-neutral-200 pb-2">
                Dog Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="dogName" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Name <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="dogName"
                    name="dogName"
                    value={formData.dogName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogName ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.dogName && <p className="mt-1 text-sm text-accent-600">{errors.dogName}</p>}
                </div>

                <div>
                  <label htmlFor="dogBreed" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Breed <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="dogBreed"
                    name="dogBreed"
                    value={formData.dogBreed}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogBreed ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.dogBreed && <p className="mt-1 text-sm text-accent-600">{errors.dogBreed}</p>}
                </div>

                <div>
                  <label htmlFor="dogAge" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Age <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="dogAge"
                    name="dogAge"
                    value={formData.dogAge}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.dogAge ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.dogAge && <p className="mt-1 text-sm text-accent-600">{errors.dogAge}</p>}
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-neutral-700 mb-1">
                    Service Dog Type <span className="text-accent-600">*</span>
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.serviceType ? 'border-accent-600' : 'border-neutral-300'}`}
                  >
                    <option value="">Select Service Type</option>
                    <option value="Mobility Support">Mobility Support</option>
                    <option value="Guide Dog">Guide Dog (Visual Impairment)</option>
                    <option value="Hearing Dog">Hearing Dog</option>
                    <option value="Psychiatric Service Dog">Psychiatric Service Dog</option>
                    <option value="Medical Alert">Medical Alert/Response</option>
                    <option value="Autism Support">Autism Support</option>
                    <option value="Seizure Response">Seizure Response</option>
                    <option value="Other">Other (Please specify)</option>
                  </select>
                  {errors.serviceType && <p className="mt-1 text-sm text-accent-600">{errors.serviceType}</p>}
                </div>

                <div>
                  <label htmlFor="dogPhoto" className="block text-sm font-medium text-neutral-700 mb-1">
                    Dog Photo
                  </label>
                  <div className="flex items-center space-x-4">
                    {photoPreview && (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-20 w-20 object-cover rounded-lg border border-neutral-300"
                      />
                    )}

                    <div>
                      <input
                        type="file"
                        id="dogPhoto"
                        name="dogPhoto"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="dogPhoto"
                        className="cursor-pointer bg-primary-50 text-primary-700 border border-primary-300 hover:bg-primary-100 px-4 py-2 rounded-lg transition-colors inline-block"
                      >
                        Change Photo
                      </label>
                    </div>
                  </div>
                  {errors.dogPhoto && <p className="mt-1 text-sm text-accent-600">{errors.dogPhoto}</p>}
                </div>
              </div>
            </div>

            {/* Right column - Owner Info */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-primary-700 mb-4 border-b border-neutral-200 pb-2">
                Owner Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="ownerName" className="block text-sm font-medium text-neutral-700 mb-1">
                    Owner Full Name <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="ownerName"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerName ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.ownerName && <p className="mt-1 text-sm text-accent-600">{errors.ownerName}</p>}
                </div>

                <div>
                  <label htmlFor="ownerAddress" className="block text-sm font-medium text-neutral-700 mb-1">
                    Address <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="ownerAddress"
                    name="ownerAddress"
                    value={formData.ownerAddress}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerAddress ? 'border-accent-600' : 'border-neutral-300'}`}
                  ></textarea>
                  {errors.ownerAddress && <p className="mt-1 text-sm text-accent-600">{errors.ownerAddress}</p>}
                </div>

                <div>
                  <label htmlFor="ownerPhone" className="block text-sm font-medium text-neutral-700 mb-1">
                    Phone Number <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="tel"
                    id="ownerPhone"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerPhone ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.ownerPhone && <p className="mt-1 text-sm text-accent-600">{errors.ownerPhone}</p>}
                </div>

                <div>
                  <label htmlFor="ownerEmail" className="block text-sm font-medium text-neutral-700 mb-1">
                    Email Address <span className="text-accent-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="ownerEmail"
                    name="ownerEmail"
                    value={formData.ownerEmail}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerEmail ? 'border-accent-600' : 'border-neutral-300'}`}
                  />
                  {errors.ownerEmail && <p className="mt-1 text-sm text-accent-600">{errors.ownerEmail}</p>}
                </div>

                <div>
                  <label htmlFor="registryNumber" className="block text-sm font-medium text-neutral-700 mb-1">
                    Registry Number
                  </label>
                  <input
                    type="text"
                    id="registryNumber"
                    value={formData.registryNumber}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="mt-1 text-xs text-neutral-500">Registry numbers cannot be changed</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-8 pt-4 border-t border-neutral-200">
            <div className="text-neutral-600 text-sm">
              <p>
                <span className="font-semibold">Registration Date:</span> {new Date(formData.registrationDate).toLocaleDateString()}
              </p>
              <p>
                <span className="font-semibold">Last Updated:</span> {new Date(formData.lastUpdated).toLocaleDateString()}
              </p>
            </div>

            <button
              type="submit"
              disabled={saving || !hasChanges}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEdit;